import React, { useEffect, useState } from 'react';
import { getSuccessStories } from '../services/apiService';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Quote, 
  Star, 
  Trophy, 
  ArrowRight, 
  GraduationCap 
} from 'lucide-react';
const SuccessStories = () => {

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');

  // --- FETCH DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getSuccessStories();
        // Filter out Visa and IELTS stories, keep only Academic/Training stories
        const academicStories = data.filter(
          (s) => s.category !== 'visa' && s.category !== 'ielts' && s.type !== 'visa' && s.type !== 'ielts'
        );
        // Filter out stories with blob URLs
        const validStories = academicStories.filter(story => {
          const url = story.photo || story.photo_url || story.imageUrl;
          return !url || !url.startsWith('blob:');
        });
        setStories(validStories);
      } catch (error) {
        console.error("Failed to load stories", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- FILTER LOGIC ---
  // Extract unique categories (e.g., 'IELTS', 'OET', 'PTE') from the data
  const categories = ['All', ...new Set(stories.map(s => s.category || 'General').filter(Boolean))];

  const displayStories = activeFilter === 'All' 
    ? stories 
    : stories.filter(s => (s.category || 'General') === activeFilter);

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      

      {/* =========================================
          HERO SECTION
      ========================================= */}
      <section className="relative pt-32 pb-20 bg-white border-b border-slate-200 overflow-hidden">
        {/* Background Decor */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-red-600 via-red-500 to-red-600"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-red-50 rounded-full blur-3xl opacity-50 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-bold uppercase tracking-widest mb-6">
              <Trophy size={14} className="text-yellow-500" /> Student Achievements
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 mb-6">
              The Hall of <span className="text-red-600">Fame.</span>
            </h1>
            <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
              From Band 8.5 in IELTS to perfect 90s in PTE. <br/>
              See how our students are crushing their academic goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* =========================================
          FILTER BAR (Sticky & Scrollable)
      ========================================= */}
      <div className="top-[70px] z-30 bg-white/80 backdrop-blur-md border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-nowrap overflow-x-auto no-scrollbar gap-3 md:justify-center">
             {/* If no categories found (loading or empty), show skeleton */}
             {loading && (
               [1,2,3].map(i => <div key={i} className="w-24 h-10 bg-slate-100 rounded-full animate-pulse" />)
             )}

             {!loading && categories.map(cat => (
               <button
                 key={cat}
                 onClick={() => setActiveFilter(cat)}
                 className={`px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all border ${
                   activeFilter === cat
                     ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                     : 'bg-white text-slate-600 border-slate-200 hover:border-red-500 hover:text-red-500'
                 }`}
               >
                 {cat === 'All' ? 'All Stories' : cat}
               </button>
             ))}
          </div>
        </div>
      </div>

      {/* =========================================
          STORIES GRID
      ========================================= */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* LOADING STATE */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white h-96 rounded-2xl border border-slate-100 shadow-sm p-6 animate-pulse flex flex-col">
                  <div className="w-16 h-16 bg-slate-200 rounded-full mb-6"></div>
                  <div className="h-4 bg-slate-200 rounded w-3/4 mb-3"></div>
                  <div className="h-3 bg-slate-200 rounded w-1/2 mb-8"></div>
                  <div className="flex-1 bg-slate-50 rounded-xl"></div>
                </div>
              ))}
            </div>
          )}

          {/* CONTENT STATE */}
          {!loading && (
            <>
              {displayStories.length > 0 ? (
                <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence>
                    {displayStories.map((story, index) => (
                      <motion.div
                        layout
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        key={story.id || index}
                        className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-red-100 hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col h-full"
                      >
                        
                        {/* 1. CARD HEADER: IMAGE & INFO */}
                        <div className="p-6 pb-4 flex items-start gap-4 border-b border-slate-50">
                          <div className="w-14 h-14 rounded-full bg-slate-100 overflow-hidden border border-slate-200 flex items-center justify-center shrink-0">
                            {(() => {
                              const url = story.photo || story.photo_url || story.imageUrl;
                              const safeUrl = url && !url.startsWith('blob:') ? url : null;
                              if (safeUrl) {
                                return (
                                  <img 
                                    src={safeUrl} 
                                    alt={story.name} 
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                  />
                                );
                              }
                              return (
                                <span className="text-xl font-bold text-red-600">
                                  {(story.name || 'S').charAt(0)}
                                </span>
                              );
                            })()}
                            <span 
                              className="hidden text-xl font-bold text-red-600"
                              style={{ display: 'none' }}
                            >
                              {(story.name || 'S').charAt(0)}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-slate-900 leading-tight">
                              {story.name || "Student Success"}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                               <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                                 {story.category || "Alumni"}
                               </span>
                               {story.course && (
                                 <>
                                  <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                  <span className="text-xs text-slate-500 font-medium truncate max-w-[120px]">
                                    {story.course}
                                  </span>
                                 </>
                               )}
                            </div>
                          </div>
                        </div>

                        {/* 2. CARD BODY: THE "SCORE" HIGHLIGHT */}
                        <div className="p-6 flex-1 flex flex-col relative">
                          <Quote className="absolute top-6 right-6 text-slate-100" size={40} />
                          
                          {/* If story text is short, make it large. If long, keep it normal. */}
                          <p className={`relative z-10 text-slate-600 italic leading-relaxed mb-6 ${
                            (story.story?.length || 0) < 100 ? 'text-lg' : 'text-sm'
                          }`}>
                            "{story.story || story.testimonial || "Training at 6th Sense was a game changer for my career."}"
                          </p>
                          
                          {/* RESULT BADGE (The "Dopamine" Hit) */}
                          <div className="mt-auto pt-4 flex items-center justify-between">
                            <div className="flex gap-1">
                              {[1,2,3,4,5].map(i => (
                                <Star key={i} size={14} className="text-yellow-400 fill-current" />
                              ))}
                            </div>
                            <div className="px-3 py-1 bg-red-50 text-red-700 rounded-lg text-xs font-bold uppercase tracking-wider border border-red-100 flex items-center gap-1">
                              <Trophy size={12} />
                              Top Scorer
                            </div>
                          </div>
                        </div>

                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
              ) : (
                /* EMPTY STATE */
                <div className="text-center py-32 bg-white rounded-3xl border border-dashed border-slate-300">
                  <div className="inline-block p-4 bg-slate-50 rounded-full mb-4">
                    <GraduationCap size={40} className="text-slate-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No stories found</h3>
                  <p className="text-slate-500">
                    We haven't uploaded stories for this category yet.
                  </p>
                  <button 
                    onClick={() => setActiveFilter('All')} 
                    className="mt-6 text-red-600 font-bold text-sm hover:underline"
                  >
                    View All Stories
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </section>

      {/* =========================================
          CTA SECTION
      ========================================= */}
      <section className="py-20 bg-slate-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-gray-100 text-3xl md:text-5xl font-serif font-bold mb-6">
            Your name belongs on this wall.
          </h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
            Join the academy that produces top rankers consistently. <br className="hidden md:block"/>
            Start your preparation with a free demo class today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-free-demo-class"
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold transition-all hover:scale-105 shadow-xl shadow-red-900/20"
            >
              Book Free Demo Class <ArrowRight size={18} />
            </Link>
            <Link
              to="/book-free-counselling"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl font-bold border border-white/10 transition-all backdrop-blur-sm"
            >
              Talk to a Mentor
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default SuccessStories;